import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateCategoryName } from '@/lib/category-i18n';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, RotateCw } from 'lucide-react';
import { StatusBadge } from './status-badge';
import { HoldBackBadge } from './hold-back-badge';
import { AIAnalysisPanel } from './ai-analysis-panel';
import { InvestmentEnrichmentForm } from './investment-enrichment-form';
import { isInvestmentEnrichmentComplete, requiresTicker } from '@/lib/investment-utils';
import { TagInput } from '@/components/entities/tag-input';
import { useTags, useCreateTag } from '@/hooks/use-tags';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { ReviewItem } from './types';
import type { Category } from '@/types/api';

interface DeepDiveDrawerProps {
  item: ReviewItem | null;
  categories: Category[];
  accounts?: { id: number; name: string }[];
  onClose: () => void;
  onSaveAndPromote: (data: DrawerSaveData) => void;
  onSkip: () => void;
  /**
   * Move a CONFIRMED import row back to PENDING. Only shown for
   * `item.source === 'import'` where `promotionStatus === 'CONFIRMED'`.
   * Motivating use case: auto-promote fires at the default 0.90
   * confidence threshold, but a row landing at e.g. 0.91 may still
   * deserve a manual look before commit — this escape hatch lets the
   * user demote it without having to commit-then-edit-the-transaction
   * after the fact. If not provided, the button is hidden.
   */
  onResetToPending?: () => void;
  isSaving?: boolean;
  /** Retries classification for a FAILED PlaidTransaction. Only shown when
   *  item.originalPlaidTx.promotionStatus === 'FAILED'. */
  onRetry?: () => void;
  isRetrying?: boolean;
}

/** Maps a raw backend processingError message to an i18n key with user-friendly copy. */
function getHumanizedErrorKey(message: string | null | undefined): string {
  if (!message) return 'review.errorGeneric';
  const lower = message.toLowerCase();
  if (lower.includes('timed out') || lower.includes('timeout')) return 'review.errorTimedOut';
  if (lower.includes('rate limit')) return 'review.errorRateLimited';
  return 'review.errorGeneric';
}

export interface DrawerSaveData {
  item: ReviewItem;
  categoryId: number | null;
  accountId: number | null;
  ticker: string;
  assetQuantity: string;
  assetPrice: string;
  details: string;
  // Ticker resolution metadata (Sprint 14)
  isin?: string;
  exchange?: string;
  assetCurrency?: string;
  tagNames: string[];
}

export function DeepDiveDrawer({
  item,
  categories,
  accounts,
  onClose,
  onSaveAndPromote,
  onSkip,
  onResetToPending,
  isSaving,
  onRetry,
  isRetrying,
}: DeepDiveDrawerProps) {
  const { t } = useTranslation();
  const { data: tags = [] } = useTags();
  const createTag = useCreateTag();

  // ── Local form state ─────────────────────────────────────────────────
  const [drawerCategory, setDrawerCategory] = useState<number | null>(null);
  const [drawerAccountId, setDrawerAccountId] = useState<number | null>(null);
  const [ticker, setTicker] = useState('');
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [details, setDetails] = useState('');
  // Ticker resolution metadata (Sprint 14)
  const [tickerIsin, setTickerIsin] = useState('');
  const [tickerExchange, setTickerExchange] = useState('');
  const [tickerAssetCurrency, setTickerAssetCurrency] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Reset state when item changes
  useEffect(() => {
    if (item) {
      setDrawerCategory(item.categoryId);
      setDrawerAccountId(item.originalImportRow?.accountId ?? null);
      // Use || to catch 0, "", and null/undefined — all treated as "no value".
      // Also validate ticker contains at least one letter (pure numeric like "0" is invalid).
      const rawTicker = item.originalImportRow?.ticker || item.originalPlaidTx?.ticker || '';
      setTicker(rawTicker && /[a-zA-Z]/.test(rawTicker) ? rawTicker : '');
      // For qty/price, explicitly filter out zero values (Prisma Decimal 0 is not meaningful pre-fill)
      const rawQty = item.originalImportRow?.assetQuantity ?? item.originalPlaidTx?.assetQuantity;
      setQty(rawQty != null && Number(rawQty) !== 0 ? String(rawQty) : '');
      const rawPrice = item.originalImportRow?.assetPrice ?? item.originalPlaidTx?.assetPrice;
      setPrice(rawPrice != null && Number(rawPrice) !== 0 ? String(rawPrice) : '');
      setDetails('');
      // Pre-fill ticker resolution metadata if available
      setTickerIsin(item.originalImportRow?.isin || '');
      setTickerExchange(item.originalImportRow?.exchange || '');
      setTickerAssetCurrency(item.originalImportRow?.assetCurrency || '');

      // Tags: pre-fill from CSV-parsed row.tags (name strings) for import rows;
      // empty for Plaid (PlaidTransaction has no tags field). TagInput is
      // ID-based, so any CSV name with no matching Tag row is eagerly
      // created here — mirrors the lazy create resolveTagsByName() already
      // does server-side at commit time, just moved earlier.
      const rawTagNames = item.originalImportRow?.tags ?? [];
      if (rawTagNames.length > 0) {
        const resolvedIds: number[] = [];
        const unmatchedNames: string[] = [];
        for (const name of rawTagNames) {
          const match = tags.find((tg) => tg.name.toLowerCase() === name.toLowerCase());
          if (match) {
            resolvedIds.push(match.id);
          } else {
            unmatchedNames.push(name);
          }
        }
        setSelectedTagIds(resolvedIds);
        if (unmatchedNames.length > 0) {
          Promise.all(unmatchedNames.map((name) => createTag.mutateAsync({ name })))
            .then((created) => {
              setSelectedTagIds((prev) => [...prev, ...created.map((tg) => tg.id)]);
            })
            .catch(() => {});
        }
      } else {
        setSelectedTagIds([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, tags]);

  if (!item) return null;

  // Dynamic investment enrichment detection
  const INVESTMENT_HINTS_MANDATORY = new Set(['API_STOCK', 'API_CRYPTO', 'API_FUND']);
  const INVESTMENT_HINTS_OPTIONAL = new Set(['MANUAL']);
  const selectedCat = categories.find((c) => c.id === drawerCategory);
  const isInvestmentCategory = selectedCat?.type === 'Investments';
  const processingHint = selectedCat?.processingHint ?? '';

  const showEnrichment =
    item.requiresEnrichment ||
    (isInvestmentCategory && (INVESTMENT_HINTS_MANDATORY.has(processingHint) || INVESTMENT_HINTS_OPTIONAL.has(processingHint)));

  // Enrichment is mandatory for API_STOCK / API_CRYPTO / API_FUND, optional for
  // MANUAL. The built-in Funds category may be ticker-less (private / unlisted
  // funds) — shared rule; ETFs always need a ticker.
  const enrichmentMandatory =
    showEnrichment && INVESTMENT_HINTS_MANDATORY.has(processingHint);
  const tickerRequired = requiresTicker(selectedCat);
  const enrichmentMissing =
    enrichmentMandatory &&
    !isInvestmentEnrichmentComplete(selectedCat, { ticker: ticker.trim(), assetQuantity: qty.trim(), assetPrice: price.trim() });

  // Account is missing when it's an import row with no accountId
  const accountMissing = item.source === 'import' && !drawerAccountId;

  const isOpen = item !== null;
  const isIncome = item.amount < 0;
  const isAlreadyProcessed =
    item.promotionStatus === 'PROMOTED' ||
    item.promotionStatus === 'CONFIRMED' ||
    item.promotionStatus === 'SKIPPED';

  const handleSave = () => {
    onSaveAndPromote({
      item,
      categoryId: drawerCategory,
      accountId: drawerAccountId,
      ticker,
      assetQuantity: qty,
      assetPrice: price,
      details,
      isin: tickerIsin || undefined,
      exchange: tickerExchange || undefined,
      assetCurrency: tickerAssetCurrency || undefined,
      tagNames: tags.filter((tg) => selectedTagIds.includes(tg.id)).map((tg) => tg.name),
    });
  };

  // Can't promote if mandatory enrichment is missing or account is missing (for non-already-processed)
  const saveDisabled = isSaving || enrichmentMissing || (accountMissing && !isAlreadyProcessed);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[min(480px,92vw)] p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
          <div>
            <SheetTitle className="text-lg">{item.merchant}</SheetTitle>
            <SheetDescription className="text-sm mt-1">
              {formatDate(item.date)} &middot;{' '}
              <span className={isIncome ? 'text-positive' : 'text-negative'}>
                {isIncome ? '+' : '-'}
                {formatCurrency(Math.abs(item.amount), item.currency)}
              </span>
            </SheetDescription>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Category selector */}
            <Select
              value={drawerCategory?.toString() ?? 'none'}
              onValueChange={(v) => {
                if (v !== 'none') setDrawerCategory(Number(v));
              }}
            >
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <SelectValue placeholder={t('review.selectCategoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" disabled>
                  {t('review.uncategorized')}
                </SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {translateCategoryName(t, cat)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <StatusBadge status={item.status} />
            <HoldBackBadge reasons={item.holdBackReasons} />
          </div>
        </SheetHeader>

        <Separator />

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Account selector — shown for import rows, especially when account is unresolved */}
          {item.source === 'import' && accounts && accounts.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                {t('review.account')}
                {accountMissing && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 border-warning/30 text-warning">
                    <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                    {t('review.required')}
                  </Badge>
                )}
              </label>
              <Select
                value={drawerAccountId?.toString() ?? 'none'}
                onValueChange={(v) => {
                  if (v !== 'none') setDrawerAccountId(Number(v));
                }}
              >
                <SelectTrigger className={`w-full h-8 text-xs ${accountMissing ? 'border-warning/50' : ''}`}>
                  <SelectValue placeholder={t('review.selectAccount')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>
                    {t('review.noAccount')}
                  </SelectItem>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id.toString()}>
                      {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Classification failure — retry or categorize manually below */}
          {item.originalPlaidTx?.promotionStatus === 'FAILED' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-destructive/10 border border-destructive/20 text-xs text-destructive">
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t(getHumanizedErrorKey(item.originalPlaidTx.processingError))}</span>
              </div>
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1 shrink-0 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={onRetry}
                  disabled={isRetrying}
                >
                  {isRetrying
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <RotateCw className="h-3.5 w-3.5" />}
                  {t('review.retryClassification')}
                </Button>
              )}
            </div>
          )}

          {/* AI Analysis */}
          <AIAnalysisPanel item={item} />

          {/* Investment Enrichment (conditional — dynamic based on selected category) */}
          {showEnrichment && (
            <>
              {enrichmentMandatory && enrichmentMissing && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-warning/10 border border-warning/20 text-xs text-warning">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t(tickerRequired ? 'review.investmentEnrichmentRequired' : 'review.investmentEnrichmentRequiredTickerOptional')}
                </div>
              )}
              <InvestmentEnrichmentForm
                ticker={ticker}
                qty={qty}
                price={price}
                details={details}
                amount={Math.abs(item.amount)}
                searchType={processingHint === 'API_CRYPTO' ? 'crypto' : undefined}
                tickerOptional={enrichmentMandatory && !tickerRequired}
                onTickerChange={setTicker}
                onQtyChange={setQty}
                onPriceChange={setPrice}
                onDetailsChange={setDetails}
                onTickerSelect={(result) => {
                  const isCrypto = processingHint === 'API_CRYPTO';
                  // Capture ticker resolution metadata from search result
                  setTickerExchange(result.mic_code || result.exchange || '');
                  // For crypto, use transaction currency (price pair determined by account, e.g. BTC/EUR)
                  setTickerAssetCurrency(isCrypto ? item.currency : (result.currency || ''));
                  // ISIN is not available from symbol_search — will be resolved server-side
                  setTickerIsin('');
                }}
              />
              {/* Currency mismatch warning — non-blocking since the transaction already exists */}
              {tickerAssetCurrency && tickerAssetCurrency !== item.currency && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-warning/10 border border-warning/20 text-xs text-warning">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t('review.currencyMismatch', { assetCurrency: tickerAssetCurrency, txCurrency: item.currency })}
                </div>
              )}
            </>
          )}

          {/* Details override for non-enrichment items */}
          {!showEnrichment && (
            <div className="space-y-1.5">
              <label htmlFor="drawer-details-simple" className="text-xs font-medium text-muted-foreground">
                {t('review.detailsNotes')}
              </label>
              <textarea
                id="drawer-details-simple"
                placeholder={t('review.optionalNotes')}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="w-full text-sm min-h-[60px] resize-none rounded-md border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          )}

          {/* Tags */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t('review.tags')}
            </label>
            <TagInput selectedTagIds={selectedTagIds} onChange={setSelectedTagIds} />
          </div>
        </div>

        {/* Footer — always show save button */}
        <Separator />
        <SheetFooter className="px-6 py-4 flex-row justify-end gap-2">
          {/* Reset to pending — only for CONFIRMED import rows. Lets the
              user undo an auto-promote without having to commit then
              edit the transaction after the fact. Hidden when the
              caller doesn't wire onResetToPending, so Plaid-review
              usage is unaffected. */}
          {onResetToPending
            && item.source === 'import'
            && item.promotionStatus === 'CONFIRMED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onResetToPending();
                onClose();
              }}
              title={t('review.resetToPendingDesc')}
            >
              {t('review.resetToPending')}
            </Button>
          )}
          {!isAlreadyProcessed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onSkip();
                onClose();
              }}
            >
              {t('review.skip')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saveDisabled}>
            {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {isAlreadyProcessed ? t('review.saveChanges') : t('review.saveAndPromote')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
